import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import botify from '../../api/botify';
import handoverTypes from 'dashboard/routes/dashboard/botify/settings/handoverTypes';
import preHandoverOptions from 'dashboard/routes/dashboard/botify/settings/preHandoverOptions';
import { ExceptionWithMessage } from 'helpers/CustomErrors';
import CreativityTypes from 'dashboard/routes/dashboard/botify/settings/creativityTypes';
import LanguageTypes from 'dashboard/routes/dashboard/botify/settings/LanguageTypes';
import KnowledgeTypes from 'dashboard/routes/dashboard/botify/settings/KnowledgeTypes';
import ResponseLength from '../../routes/dashboard/botify/settings/ResponseLengthTypes';
import { CorpusStatusService, CORPUS_STATUS } from 'dashboard/services/corpusStatusService';

const defaultCrawlStatus = {
  discoveredUrls: 0,
  crawledUrls: 0,
  indexedUrls: 0,
  failedUrls: 0,
  status: 'PENDING',
};

const state = {
  records: null,
  crawlStatus: null,
  uiFlags: {
    isInitialized: false,
    isCreated: false,
    exists: false,
    isFetching: false,
    isUpdating: false,
    isCreating: false,
    isFetchingCrawlStatus: false,
    lastFetchedAt: 0,
    lastFetchedFor: '',
    fetchError: null,
  },
  limits: {
    url: 50,
    text: 250,
    file: 50,
    qa: 500,
    product: 2000,
  },
  default: {
    name: 'باتیفای',
    avatar: null,
    corpora: [],
    messages: {
      welcome: 'سلام، چطور می توانم به شما کمک کنم؟',
      handover: 'لطفا منتظر بمانید',
      noHandover:
        'متاسفانه جواب سوال شما را نمی‌دانم. اگر سوال دیگری دارید در خدمتم.',
      unable:
        'در حال اتصال شما به یکی از کارشناسان پشتیبانی هستم تا بهتر بتوانیم کمکتان کنیم. لطفا چند لحظه منتظر بمانید.',
      selectable:
        'متاسفانه پاسخ سوال شما را نمی‌دانم. می‌خواهید گفتگو را با من ادامه دهید یا به کارشناسان متصل شوید',
      showWelcome: true,
      handoverType: handoverTypes.NO_HANDOVER,
      preHandoverFields: {
        content: 'لطفا فرم زیر را پیش از ارتباط با کارشناس پر کنید.',
        contentType: 'form',
        contentAttributes: {
          items: [
            {
              name: 'نام',
              type: 'text',
              enabled: false,
              required: false,
              label: 'نام و نام خانوادگی',
              placeholder: 'سینا احمدی',
            },
            {
              name: 'شماره تماس',
              type: 'text',
              enabled: false,
              required: false,
              label: 'شماره تماس',
              placeholder: '+989123456789',
            },
            {
              name: 'ایمیل',
              type: 'email',
              enabled: false,
              required: false,
              label: 'ایمیل',
              placeholder: 'hi@company.ai',
            },
            {
              name: 'پیام',
              type: 'text',
              enabled: false,
              required: false,
              label: 'درخواست مشتری',
              placeholder: 'مشکل در دریافت سفارشم دارم.',
            },
          ],
        },
      },
      enablePreHandover: preHandoverOptions.DISABLE,
      satisfactionOption: '👍',
      dissatisfactionOption: '👎',
      satisfactionMessage:
        'خوشحالم که کمکتون کردم😊\nاگه سوال دیگه\u200Cای دارید در خدمتم.',
      dissatisfactionMessage:
        'بسیار خوب. می\u200Cتونم شما رو به کارشناسان پشتیبانی متصل کنم. یا می\u200Cتونید سوالتون رو با جزئیات بیشتری بپرسید تا بهتر کمکتون کنم.',
      enableFeedback: true,
    },
    inbox_ids: [],
    intonation: 'CONVERSATIONAL',
    creativity: CreativityTypes.MEDIUM,
    responseLanguage: LanguageTypes.PERSIAN,
    knowledgeUsage: KnowledgeTypes.RESTRICTED,
    responseLength: ResponseLength.MEDIUM,
    promptConfig: {
      businessIntroduction: null,
      businessCommunicationStyle: null,
      businessHandoverPolicies: null,
      businessClarificationGuides: null,
    },
  },
};

export const getters = {
  getCorpora($state) {
    return ($state.records?.corpora || []).filter(c => c.status !== 'DELETED');
  },
  getURLCorpora($state) {
    return getters.getCorpora($state).filter(corpus => corpus.type === 'URL');
  },
  getTextCorpora($state) {
    return getters.getCorpora($state).filter(corpus => corpus.type === 'TEXT');
  },
  getFileCorpora($state) {
    return getters.getCorpora($state).filter(corpus => corpus.type === 'FILE');
  },
  getQaCorpora($state) {
    return getters
      .getCorpora($state)
      .filter(corpus => corpus.type === 'FAQ')
      .flatMap(corpus => corpus.faq || []);
  },
  getCorpusStatus($state) {
    return (corpusId) => {
      const corpus = getters.getCorpora($state).find(c => c.id === corpusId);
      return corpus?.status || 'UNKNOWN';
    };
  },
  getInboxIds($state) {
    return $state.records?.inbox_ids || [];
  },
  getLimits($state) {
    return $state.limits;
  },
  getSettings($state) {
    if (!$state.uiFlags.isInitialized || !$state.records) return {};
    const { corpora = [], ...settings } = $state.records;
    return settings;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getIsCreated($state) {
    return $state.uiFlags.isCreated;
  },
  checkCorporaLimit($state) {
    return (type, amount = 1) => {
      switch (type) {
        case 'url':
          if (
            !(
              getters.getURLCorpora($state).length + amount <=
              getters.getLimits($state).url
            )
          )
            throw new ExceptionWithMessage(`URL_LIMIT_EXCEEDED`);
          break;
        case 'text':
          if (
            !(
              getters.getTextCorpora($state).length + amount <=
              getters.getLimits($state).text
            )
          )
            throw new ExceptionWithMessage(`TEXT_LIMIT_EXCEEDED`);
          break;
        case 'file':
          if (
            !(
              getters.getFileCorpora($state).length + amount <=
              getters.getLimits($state).file
            )
          )
            throw new ExceptionWithMessage(`FILE_LIMIT_EXCEEDED`);
          break;
        case 'qa':
          if (
            !(
              getters.getQaCorpora($state).length + amount <=
              getters.getLimits($state).qa
            )
          )
            throw new ExceptionWithMessage(`QA_LIMIT_EXCEEDED`);
          break;
        default:
          break;
      }
    };
  },
  getBotifyId($state) {
    return $state.records?.id;
  },
  isCorporaEmpty(_state, getters, _rootState, rootGetters) {
    const urlCount = (getters.getURLCorpora || []).length;
    const textCount = (getters.getTextCorpora || []).length;
    const fileCount = (getters.getFileCorpora || []).length;
    const qaCount = (getters.getQaCorpora || []).length;

    const allProductsMeta = rootGetters['productCorpus/allProductsMeta'] || {};
    const productCount = allProductsMeta.totalElements || 0;

    const files = rootGetters['productCorpus/files'] || [];
    const productFilesCount = files.length || 0;

    return !(
      urlCount +
      textCount +
      fileCount +
      qaCount +
      productCount +
      productFilesCount
    );
  },
  isWelcomeMessageMutable(_, __, ___, rootGetters) {
    return !rootGetters['inboxes/getWebsiteInboxes'].some(inbox => {
      return inbox.pre_chat_form_enabled;
    });
  },
  getCrawlStatus($state) {
    return $state.crawlStatus ?? defaultCrawlStatus;
  },
};

const norm = v => (v === '' ? null : v);

// Backend emits status only on the parent FAQ corpus, never on individual faq
// items. Without this enrichment, response handling would leave nested items
// with status === undefined, which normalizeStatus() maps to UNSPECIFIED — a
// processing state — so the UI would render a permanent spinner even after the
// parent corpus is COMPLETED. Inherit the parent status for items that lack
// their own, and preserve previous final states so a re-processing parent
// doesn't revert a completed item.
const enrichFaqItemStatuses = (responseCorpora, previousCorpora) => {
  if (!Array.isArray(responseCorpora)) return;

  const previousFaqById = new Map();
  (previousCorpora || []).forEach(corpus => {
    if (corpus?.type !== 'FAQ' || !Array.isArray(corpus.faq)) return;
    corpus.faq.forEach(item => {
      if (item?.id) previousFaqById.set(item.id, item);
    });
  });

  const timestamp = new Date().toISOString();

  responseCorpora.forEach(corpus => {
    if (corpus?.type !== 'FAQ' || !Array.isArray(corpus.faq)) return;

    const parentStatus = corpus.status
      ? CorpusStatusService.normalizeStatus(corpus.status)
      : CORPUS_STATUS.RUNNING;

    corpus.faq = corpus.faq.map(item => {
      if (item?.status) return item;

      const previous = item?.id ? previousFaqById.get(item.id) : null;
      if (
        previous?.status &&
        !CorpusStatusService.isProcessingStatus(previous.status)
      ) {
        return {
          ...item,
          status: previous.status,
          lastStatusUpdate: previous.lastStatusUpdate || timestamp,
        };
      }

      return {
        ...item,
        status: parentStatus,
        lastStatusUpdate: timestamp,
      };
    });
  });
};

function toTexts(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (v && typeof v === 'object' && 'name' in v && 'text' in v)
    return [{ name: v.name, text: v.text }];
  return [];
}

function toUrls(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return [String(v)];
}

function toQas(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (v && typeof v === 'object' && 'question' in v && 'answer' in v)
    return [v];
  return [];
}

function adaptRequest(data = {}, isEdit = false) {
  const out = {};

  if ('name' in data && data.name != null) out.name = data.name;
  if ('del_Corpus_Ids' in data && data.del_Corpus_Ids) out.delCorpusIds = data.del_Corpus_Ids;
  if ('del_Qa_Ids' in data && data.del_Qa_Ids) out.delQaIds = data.del_Qa_Ids;
  if ('inbox_ids' in data) out.inboxIds = data.inbox_ids;
  if ('del_avatar' in data) out.delAvatar = data.del_avatar;
  if ('knowledgeUsage' in data) out.knowledgeUsage = data.knowledgeUsage;
  if ('responseLength' in data) out.responseLength = data.responseLength;
  if (isEdit && 'responseLanguage' in data) out.responseLanguage = data.responseLanguage;
  if (!isEdit && ('responseLanguage' in data || 'language' in data)) out.language = data.responseLanguage ?? data.language;
  if ('intonation' in data) out.intonation = data.intonation;
  if ('creativity' in data) out.creativity = data.creativity;
  if ('url' in data || 'urls' in data) {
    const urls = toUrls(data.urls ?? data.url);
    if (urls.length) out.urls = urls;
  }
  if ('text' in data || 'texts' in data) {
    const texts = toTexts(data.texts ?? data.text);
    if (texts.length) out.texts = texts;
  }
  if (isEdit && ('qa' in data || 'qaEditDTO' in data)) {
    const qas = toQas(data.qaEditDTO ?? data.qa).map(({ id, question, answer, isExtracted }) => ({
      id: id || null,
      question,
      answer,
      isExtracted: isExtracted || false,
    }));
    if (qas.length) out.qaEditDTO = qas;
  }
  if (!isEdit && ('qa' in data || 'qas' in data)) {
    const qas = toQas(data.qas ?? data.qa).map(({ id, question, answer, isExtracted }) => ({
      id: id || null,
      question,
      answer,
      isExtracted: isExtracted || false,
    }));
    if (qas.length) out.qas = qas;
  }

  if ('messages' in data && data.messages && typeof data.messages === 'object') {
    const m = data.messages;

    if ('showWelcome' in m) out.showWelcome = m.showWelcome;
    if ('welcome' in m) out.welcomeMessage = norm(m.welcome);
    if ('handoverType' in m) out.handoverType = m.handoverType;

    if ('handover' in m) {
      out.handoverMessage = norm(m.handover);
    }
    if ('noHandover' in m) out.noHandoverMessage = norm(m.noHandover);
    if ('selectable' in m) out.selectableMessage = norm(m.selectable);
    if ('unable' in m) out.unableMessage = norm(m.unable);

    if ('preHandoverFields' in m) out.preHandoverForm = m.preHandoverFields;

    if ('enablePreHandover' in m) {
      if (isEdit) out.enablePreHandoverForm = m.enablePreHandover;
      else out.enablePreHandover = m.enablePreHandover;
    }

    if ('satisfactionOption' in m) out.satisfactionOption = m.satisfactionOption;
    if ('dissatisfactionOption' in m) out.dissatisfactionOption = m.dissatisfactionOption;
    if ('satisfactionMessage' in m) out.satisfactionMessage = norm(m.satisfactionMessage);
    if ('dissatisfactionMessage' in m) out.dissatisfactionMessage = norm(m.dissatisfactionMessage);
    if ('enableFeedback' in m) out.enableFeedback = m.enableFeedback;
    if ('handoverButtonEnabled' in m) out.handoverButtonEnabled = m.handoverButtonEnabled;
    if ('handoverButtonText' in m) out.handoverButtonText = norm(m.handoverButtonText);
  }

  const pc = {};
  const nested = data.promptConfig && typeof data.promptConfig === 'object' ? data.promptConfig : {};
  const setPc = (key, val) => {
    if (val !== undefined) pc[key] = val;
  };

  setPc('businessIntroduction', data.businessIntroduction ?? nested.businessIntroduction);
  setPc('businessCommunicationStyle', data.businessCommunicationStyle ?? nested.businessCommunicationStyle);
  setPc('businessHandoverPolicies', data.businessHandoverPolicies ?? nested.businessHandoverPolicies);
  setPc('businessClarificationGuides', data.businessClarificationGuides ?? nested.businessClarificationGuides);

  if (Object.keys(pc).length) out.promptConfig = pc;

  return out;
}

function currentAccountIdFromUrl() {
  if (typeof window === 'undefined') return '';
  if (!window.location.pathname.includes('/app/accounts')) return '';
  return window.location.pathname.split('/')[3] || '';
}

export const actions = {
  get: async ({ commit, state: $state }, accountId) => {
    // Capture the account at dispatch time so a response cannot be committed
    // against a different account if the user switches mid-flight.
    const scopedAccountId = accountId || currentAccountIdFromUrl();
    commit(types.SET_BOTIFY_UI_FLAG, { isFetching: true, fetchError: null });
    try {
      const response = await botify.get();

      // Drop stale responses: the active account changed while in flight.
      if (currentAccountIdFromUrl() !== scopedAccountId) return null;

      if (response.data?.corpora) {
        const previousCorpora = $state.records?.corpora || [];
        response.data.corpora = response.data.corpora.map(corpus => ({
          ...corpus,
          status: corpus.status || 'UNKNOWN',
          lastStatusUpdate: corpus.lastStatusUpdate || null,
        }));
        enrichFaqItemStatuses(response.data.corpora, previousCorpora);
      }

      commit(types.SET_BOTIFY, response.data);
      commit(types.SET_BOTIFY_UI_FLAG, {
        isFetching: false,
        isInitialized: true,
        isCreated: true,
        exists: true,
        lastFetchedAt: Date.now(),
        lastFetchedFor: scopedAccountId,
        fetchError: null,
      });
      return response.data;
    } catch (error) {
      // Drop stale errors too: the active account changed while in flight.
      if (currentAccountIdFromUrl() !== scopedAccountId) return null;

      const status = error?.response?.status;
      if (status === 404) {
        commit(types.SET_BOTIFY, null);
        commit(types.SET_BOTIFY_UI_FLAG, {
          isFetching: false,
          isInitialized: true,
          isCreated: false,
          exists: false,
          lastFetchedAt: Date.now(),
          lastFetchedFor: scopedAccountId,
          fetchError: null,
        });
        return null;
      }
      commit(types.SET_BOTIFY_UI_FLAG, {
        isFetching: false,
        isInitialized: true,
        fetchError: {
          status: status || 0,
          message: error?.message || 'Network error',
        },
      });
      return null;
    }
  },

  reset: ({ commit }) => {
    commit(types.SET_BOTIFY, null);
    commit(types.SET_BOTIFY_UI_FLAG, {
      isInitialized: false,
      isCreated: false,
      exists: false,
      isFetching: false,
      isUpdating: false,
      isCreating: false,
      isFetchingCrawlStatus: false,
      lastFetchedAt: 0,
      lastFetchedFor: '',
      fetchError: null,
    });
  },

  update: async ({ commit, dispatch, state: $state }, updateObj) => {
    commit(types.SET_BOTIFY_UI_FLAG, { isUpdating: true });
    try {
      if (getters.getIsCreated($state)) {
        const previousCorpora = $state.records?.corpora || [];
        const formData = await dispatch('prepareRequest', {
          payload: updateObj,
          isUpdate: true,
        });
        const response = await botify.newUpdate($state.records.id, formData);

        if (response.data?.corpora) {
          enrichFaqItemStatuses(response.data.corpora, previousCorpora);
        }

        commit(types.SET_BOTIFY, response.data);
        commit(types.SET_BOTIFY_UI_FLAG, { isUpdating: false });
        return response.data;
      }

      commit(types.SET_BOTIFY_UI_FLAG, { isUpdating: false });
      return await dispatch('create', updateObj);
    } catch (error) {
      commit(types.SET_BOTIFY_UI_FLAG, { isUpdating: false });
      throw error;
    }
  },

  create: async ({ commit, dispatch, state: $state }, createObj) => {
    // Capture the account at dispatch time for race-safe scoping of the result.
    const accountId = currentAccountIdFromUrl();
    commit(types.SET_BOTIFY_UI_FLAG, { isCreating: true });

    try {
      const formData = await dispatch('prepareRequest', {
        payload: createObj,
        isUpdate: false,
      });
      const response = await botify.newCreate(formData);

      // Preserve status fields that were added during createCorpora
      if (response.data?.corpora) {
        const originalCorpora = $state.records?.corpora || [];
        const newCorpora = response.data.corpora;

        // Match corpora by name and preserve status
        newCorpora.forEach(newCorpus => {
          const originalCorpus = originalCorpora.find(orig =>
            orig.name === newCorpus.name && orig.type === newCorpus.type
          );

          if (originalCorpus?.status) {
            newCorpus.status = originalCorpus.status;
            newCorpus.lastStatusUpdate = originalCorpus.lastStatusUpdate;
          } else {
            // Set default status for new corpus
            newCorpus.status = CorpusStatusService.getDefaultStatus();
            newCorpus.lastStatusUpdate = new Date().toISOString();
          }
        });

        enrichFaqItemStatuses(response.data.corpora, originalCorpora);
      }

      commit(types.SET_BOTIFY, response.data);
      commit(types.SET_BOTIFY_UI_FLAG, {
        isCreating: false,
        isCreated: true,
        exists: true,
        lastFetchedAt: Date.now(),
        lastFetchedFor: accountId,
      });
      return response.data;
    } catch (error) {
      commit(types.SET_BOTIFY_UI_FLAG, { isCreating: false });

      if (error?.response?.status === 409) {
        try {
          await dispatch('get');
          await dispatch('update', createObj);
          return;
        } catch (getError) {
          throw error;
        }
      }
      throw error;
    }
  },

  prepareRequest: async ({ state: $state }, { payload, isUpdate }) => {
    const formData = new FormData();
    const raw = isUpdate ? payload : { ...$state.default, ...payload };
    const { files: _files, file: _file, avatar: _avatar, ...serializableRaw } = raw;
    const plain = JSON.parse(JSON.stringify(serializableRaw));
    if (isUpdate && !('inbox_ids' in plain) && $state.records && 'inbox_ids' in $state.records) {
      plain.inbox_ids = $state.records.inbox_ids;
    }
    const dto = adaptRequest(plain, isUpdate);

    formData.append(
      'data',
      new Blob([JSON.stringify(dto)], { type: 'application/json' })
    );

    const rawFiles = raw.files ?? raw.file ?? [];
    const files = isUpdate
      ? (Array.isArray(rawFiles) ? rawFiles.filter(f => !f?.id) : [])
      : Array.isArray(rawFiles) ? rawFiles : [];
    files.forEach(file => formData.append('file[]', file));

    if (raw.avatar && (!isUpdate || !raw.avatar.id)) {
      formData.append('avatar', raw.avatar);
    }

    return formData;
  },
  createCorpora: async (context, createObj) => {
    const corpusType = Object.keys(createObj)[0];
    const corpora = createObj[corpusType];

    // Check limits
    getters.checkCorporaLimit(context.state)(
      corpusType,
      Array.isArray(corpora) ? corpora.length : undefined
    );

    const defaultStatus = CorpusStatusService.getDefaultStatus();
    const defaultTimestamp = new Date().toISOString();
    const withStatus = obj =>
      obj && typeof obj === 'object'
        ? { ...obj, status: defaultStatus, lastStatusUpdate: defaultTimestamp }
        : obj;

    // File corpora are raw File objects — spreading them loses all data
    // (File properties are prototype getters, not own-enumerable). Skip enrichment.
    const enrichedCorpora =
      corpusType === 'file'
        ? corpora
        : Array.isArray(corpora)
        ? corpora.map(withStatus)
        : withStatus(corpora);

    await actions.update(context, { [corpusType]: enrichedCorpora });
  },
  deleteCorpora: async (context, id) => {
    await actions.update(context, { del_Corpus_Ids: [id] });
  },

  deleteQa: async (context, id) => {
    await actions.update(context, { del_Qa_Ids: [id] });
  },

  setDefault({ commit, state: $state }) {
    commit(types.SET_BOTIFY, $state.default);
    // Optimistic signal: the user has started onboarding, so the empty state
    // (NoBotify) must yield to the main view even before the bot is created.
    commit(types.SET_BOTIFY_UI_FLAG, { isInitialized: true, exists: true });
  },

  disableWelcomeMessage: async ({ dispatch, state: $state }) => {
    if ($state.uiFlags.isCreated) {
      await dispatch('update', { messages: { showWelcome: false } });
    }
  },
  /**
   * Fetch crawl status for a specific corpus
   * @param {string} corpusId - The corpus ID to fetch status for
   */
  fetchCrawlStatus: async ({ commit }, corpusId) => {
    commit(types.SET_BOTIFY_UI_FLAG, { isFetchingCrawlStatus: true });
    try {
      const response = await botify.getCorpusCrawlStatus(corpusId);
      const crawlData = {
        ...defaultCrawlStatus,
        ...response.data,
      };
      commit(types.SET_BOTIFY_CRAWL_STATUS, crawlData);
      commit(types.SET_BOTIFY_UI_FLAG, { isFetchingCrawlStatus: false });
      return crawlData;
    } catch (error) {
      commit(types.SET_BOTIFY_UI_FLAG, { isFetchingCrawlStatus: false });
      commit(types.SET_BOTIFY_CRAWL_STATUS, {
        ...defaultCrawlStatus,
        status: 'FAILED',
      });
      throw error;
    }
  },
  /**
   * Clear crawl status
   */
  clearCrawlStatus: ({ commit }) => {
    commit(types.SET_BOTIFY_CRAWL_STATUS, defaultCrawlStatus);
  },
  updateCorpusStatus: ({ commit }, { corpusId, status, lastStatusUpdate }) => {
    commit(types.UPDATE_CORPUS_STATUS, { corpusId, status, lastStatusUpdate });
  },
};

export const mutations = {
  [types.SET_BOTIFY_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_BOTIFY]: MutationHelpers.set,
  [types.EDIT_BOTIFY]($state, data) {
    $state.records = {
      ...$state.records,
      ...data,
    };
  },
  [types.UPDATE_CORPUS_STATUS]($state, { corpusId, status, lastStatusUpdate }) {
    if (!$state.records?.corpora) return;

    const corpusIndex = $state.records.corpora.findIndex(
      c => c.id === corpusId
    );

    if (corpusIndex !== -1) {
      if (status === 'DELETED') {
        $state.records.corpora.splice(corpusIndex, 1);
        return;
      }
      const existingCorpus = $state.records.corpora[corpusIndex];
      const updatedCorpus = {
        ...existingCorpus,
        status,
        lastStatusUpdate,
      };

      // Backend emits corpus-level events only. For FAQ corpora, propagate the
      // parent status down to nested faq items that are still in a processing
      // state; leave already-final items (COMPLETED/FAILED/etc.) untouched.
      if (existingCorpus.type === 'FAQ' && Array.isArray(existingCorpus.faq)) {
        updatedCorpus.faq = existingCorpus.faq.map(item =>
          CorpusStatusService.isProcessingStatus(item.status)
            ? { ...item, status, lastStatusUpdate }
            : item
        );
      }

      $state.records.corpora[corpusIndex] = updatedCorpus;
      return;
    }

    // Fallback: search nested faq[] arrays for QA items
    $state.records.corpora.some(corpus => {
      if (!corpus.faq?.length) return false;
      const faqIndex = corpus.faq.findIndex(item => item.id === corpusId);
      if (faqIndex === -1) return false;

      if (status === 'DELETED') {
        corpus.faq.splice(faqIndex, 1);
      } else {
        corpus.faq[faqIndex] = {
          ...corpus.faq[faqIndex],
          status,
          lastStatusUpdate,
        };
      }
      return true;
    });
  },
  [types.SET_BOTIFY_CRAWL_STATUS]($state, data) {
    $state.crawlStatus = data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
